import React from 'react';
import { PriceInsightBucket, TimeGranularity } from './StrategiesUtils';
import { useCategoryTerms } from '../../../utils/categoryTerms';
import { GRANULARITY_LABELS } from '../../../utils/timeGranularity';

interface Props {
  buckets: PriceInsightBucket[];
  selectedIndex?: number;
  timeGranularity?: TimeGranularity;
}

const fmtQty = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtPrice = (v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;


export const HedgePriceMonthDetail: React.FC<Props> = ({ buckets, selectedIndex, timeGranularity = 'Month' }) => {
  const terms = useCategoryTerms();
  const row = selectedIndex != null ? buckets[selectedIndex] : buckets[0];
  const isAggregated = timeGranularity !== 'Month';

  if (!row) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 items-center justify-center">
        <span className="text-sm text-gray-400">No data available</span>
      </div>
    );
  }

  const adv = row.priceAdvantage;
  const isFavorable = adv > 0;
  const covPct = row.coverageRatio * 100;

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <span style={{ color: 'rgba(59, 130, 246, 0.5)', fontSize: '0.65em' }}>&#9654;</span>
        <span className="text-base font-semibold text-gray-900">{row.label}</span>
        <span className="text-sm text-gray-400">{GRANULARITY_LABELS[timeGranularity]} Breakdown</span>
      </div>
      {isAggregated && (
        <div className="text-xs text-gray-400 mb-3 leading-tight">
          Quantities and values are aggregated across all months in this {GRANULARITY_LABELS[timeGranularity].toLowerCase()} period. Avg cost is weighted by quantity.
        </div>
      )}

      {/* Quantity breakdown */}
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Secured Stock</div>
      <div className="space-y-0 divide-y divide-gray-100 mb-3">
        <QtyPriceRow color="#10B981" label="On-Hand" qty={row.onHandQty} value={row.onHandValue} spotPrice={row.spotPrice} />
        <QtyPriceRow color="#3B82F6" label="Supply POs" qty={row.supplyQty} value={row.supplyValue} />
        <QtyPriceRow color="#8B5CF6" label={terms.hedge} qty={row.hedgeQty} value={row.hedgeValue} />
      </div>

      {/* Total secured */}
      <div className="flex items-center justify-between py-2 border-t border-gray-200 mb-1">
        <span className="text-sm font-semibold text-gray-900">Total Secured</span>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-gray-900 tabular-nums">{fmtQty(row.totalSecured)} MT</span>
          {row.totalSecured > 0 && row.weightedAvgCost > 0 && (
            <span className="text-xs font-semibold text-gray-500 tabular-nums">{fmtPrice(row.weightedAvgCost)}/MT</span>
          )}
        </div>
      </div>

      {/* Demand */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm flex-shrink-0 border-2 border-dashed" style={{ borderColor: '#EF4444' }} />
          <span className="text-sm text-gray-600">{terms.demand}</span>
        </div>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmtQty(row.demandQty)} MT</span>
      </div>

      {/* Coverage indicator */}
      {row.demandQty > 0 && (
        <div className={`p-2 rounded-lg text-xs mb-4 ${
          covPct >= 100 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
        }`}>
          {covPct >= 100
            ? `✓ ${covPct.toFixed(0)}% covered`
            : `⚠ ${covPct.toFixed(0)}% covered — ${fmtQty(row.demandQty - row.totalSecured)} MT short`
          }
        </div>
      )}

      {/* Price comparison */}
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 mt-2">Price Position</div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Weighted Avg Cost</span>
          <span className="text-sm font-bold text-gray-900 tabular-nums">
            {row.weightedAvgCost > 0 ? `${fmtPrice(row.weightedAvgCost)}/MT` : '—'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Spot Price</span>
          <span className="text-sm font-semibold text-gray-500 tabular-nums">
            {fmtPrice(row.spotPrice)}/MT
          </span>
        </div>
      </div>

      {/* Price advantage badge */}
      {row.weightedAvgCost > 0 && row.spotPrice > 0 && (
        <div className={`mt-3 p-3 rounded-lg text-sm font-medium ${
          isFavorable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {isFavorable
            ? `▲ ${adv.toFixed(1)}% below spot — saving ${fmtPrice(row.spotPrice - row.weightedAvgCost)}/MT`
            : `▼ ${Math.abs(adv).toFixed(1)}% above spot — overpaying ${fmtPrice(row.weightedAvgCost - row.spotPrice)}/MT`
          }
        </div>
      )}

    </div>
  );
};

const QtyPriceRow: React.FC<{ color: string; label: string; qty: number; value: number; spotPrice?: number }> = ({ color, label, qty, value, spotPrice }) => {
  const avgPrice = qty > 0 && value > 0 ? value / qty : 0;
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-gray-900 tabular-nums">
          {qty > 0 ? `${fmtQty(qty)} MT` : '—'}
        </span>
        {avgPrice > 0 && (
          <span className="text-xs font-medium text-gray-500 tabular-nums" title={`Total: ${fmtPrice(value)}${spotPrice ? ` • Spot: ${fmtPrice(spotPrice)}/MT` : ''}`}>
            {fmtPrice(avgPrice)}/MT
          </span>
        )}
      </div>
    </div>
  );
};
